<template>
  <transition name="fade">
    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog" @click.stop>
        <header class="dialog-header">
          <h3>Update Content</h3>
          <button class="close-button" @click="closeDialog">
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="dialog-body">
          <!-- title -->
          <div class="card form-group" :class="{ 'has-error': errors.title }">
            <label for="title" class="form-label">Title:</label>
            <input
              :disabled="loading"
              id="title"
              v-model="updatedContent.title"
              type="text"
              class="form-control"
              @input="clearError('title')"
            />
            <span v-if="errors.title" class="error-text">{{
              errors.title
            }}</span>
          </div>
          <!-- description -->
          <div
            class="card form-group"
            :class="{ 'has-error': errors.description }"
          >
            <label for="description" class="form-label">Description:</label>
            <input
              :disabled="loading"
              id="description"
              v-model="updatedContent.description"
              type="text"
              class="form-control"
              @input="clearError('description')"
            />
            <span v-if="errors.description" class="error-text">{{
              errors.description
            }}</span>
          </div>
          <!-- fileTitle -->
          <div
            class="card form-group"
            :class="{ 'has-error': errors.fileTitle }"
          >
            <label for="fileTitle" class="form-label">File Title:</label>
            <input
              :disabled="loading"
              id="fileTitle"
              v-model="updatedContent.fileTitle"
              type="text"
              class="form-control"
              @input="clearError('fileTitle')"
            />
            <span v-if="errors.fileTitle" class="error-text">{{
              errors.fileTitle
            }}</span>
          </div>
          <!-- fileLink -->
          <div
            class="card form-group"
            :class="{ 'has-error': errors.fileLink }"
          >
            <label for="fileLink" class="form-label">File Link:</label>
            <input
              :disabled="loading"
              id="fileLink"
              v-model="updatedContent.fileLink"
              type="text"
              class="form-control"
              @input="clearError('fileLink')"
            />
            <span v-if="errors.fileLink" class="error-text">{{
              errors.fileLink
            }}</span>
          </div>
          <!-- fileButtonText -->
          <div
            class="card form-group"
            :class="{ 'has-error': errors.fileButtonText }"
          >
            <label for="fileButtonText" class="form-label"
              >File Button Text:</label
            >
            <input
              :disabled="loading"
              id="fileButtonText"
              v-model="updatedContent.fileButtonText"
              type="text"
              class="form-control"
              @input="clearError('fileButtonText')"
            />
            <span v-if="errors.fileButtonText" class="error-text">{{
              errors.fileButtonText
            }}</span>
          </div>
          <!-- Type -->
          <div class="card form-group" style="display: flex">
            <label for="type" class="form-label">Type:</label>
            <select
              :disabled="loading"
              id="type"
              v-model="updatedContent.type"
              class="form-control"
              @change="clearError('type')"
            >
              <option value="banner">Banner</option>
              <option value="video">Video</option>
            </select>
          </div>
          <!-- banner -->
          <div
            v-if="updatedContent.type === 'banner'"
            class="card form-group"
            :class="{ 'has-error': errors.banner }"
          >
            <label for="banner" class="form-label">Banner:</label>
            <input
              :disabled="loading"
              id="banner"
              type="file"
              @change="handleBannerUpload"
              class="form-control"
            />
            <img
              v-if="updatedContent.banner"
              :src="updatedContent.banner"
              alt="Banner Image"
              class="form-control"
            />
            <span v-if="errors.banner" class="error-text">{{
              errors.banner
            }}</span>
          </div>
          <!-- video -->
          <div
            v-if="updatedContent.type === 'video'"
            class="card form-group"
            :class="{ 'has-error': errors.videoLink }"
          >
            <label for="videoLink" class="form-label">Video Link:</label>
            <input
              :disabled="loading"
              id="videoLink"
              v-model="updatedContent.videoLink"
              type="text"
              class="form-control"
              @input="clearError('videoLink')"
            />
            <iframe
              v-if="getEmbedUrl(updatedContent.videoLink)"
              :src="getEmbedUrl(updatedContent.videoLink)"
              width="100%"
              height="200"
              frameborder="0"
              allowfullscreen
              class="rounded-t-xl"
            ></iframe>
            <span v-if="errors.videoLink" class="error-text">{{
              errors.videoLink
            }}</span>
          </div>
          <!-- Content -->
          <div class="card form-group" :class="{ 'has-error': errors.content }">
            <label for="content" class="form-label">Content:</label>
            <ckEditor
              :disabled="loading"
              id="content"
              :contentProp="updatedContent.content"
              @updateContent="updateContentValue"
            />
            <span v-if="errors.content" class="error-text">{{
              errors.content
            }}</span>
          </div>
          <!-- Action Button -->
          <button
            class="update-button"
            @click="handleUpdateContent"
            :disabled="loading"
          >
            <span v-if="loading" class="spinner"></span>
            <span v-else>Update</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from "vue";

// Api
import axios from "axios";
import { getBaseUrl } from "@/helper/constant.js";

import ckEditor from "@/components/ckEditor.vue";

const props = defineProps(["openDialog", "initialContentData"]);
const emit = defineEmits(["close", "update"]);

const showDialog = ref(false);
const updatedContent = ref({ ...props.initialContentData });
const errors = ref({});
const loading = ref(false);

const updateContentValue = (newContent) => {
  updatedContent.value.content = newContent;
};
const openDialog = () => {
  showDialog.value = true;
  updatedContent.value = { ...props.initialContentData }; // Ensure updatedContent is set when dialog opens
};
const closeDialog = () => {
  showDialog.value = false;
  emit("close");
};
const updateDialog = () => {
  showDialog.value = false;
  updatedContent.value = null;
  errors.value = {};
  emit("update");
};
const handleBannerUpload = (event) => {
  const file = event.target.files[0];
  const validExtensions = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  if (!validExtensions.includes(file.type)) {
    errors.value.banner = "Only image files (jpg, jpeg, png, gif) are allowed.";
    updatedContent.value.banner = "";
    return;
  }
  clearError("banner");
  const reader = new FileReader();
  reader.onload = (e) => {
    updatedContent.value.banner = e.target.result;
  };
  reader.readAsDataURL(file);
};
const getEmbedUrl = (url) => {
  let videoId;
  if (url.includes("youtube.com")) {
    videoId = url.split("v=")[1];
    const ampersandPosition = videoId ? videoId.indexOf("&") : -1;
    if (ampersandPosition !== -1) {
      videoId = videoId.substring(0, ampersandPosition);
    }
  } else if (url.includes("youtu.be")) {
    videoId = url.split("/").pop();
  } else {
    return null; // Return null for non-YouTube URLs
  }
  return `https://www.youtube.com/embed/${videoId}`;
};
const validateInputs = () => {
  errors.value = {};
  if (!updatedContent.value.type.trim())
    errors.value.type = "Type is required.";
  if (!updatedContent.value.title.trim())
    errors.value.title = "Title is required.";
  if (!updatedContent.value.description.trim())
    errors.value.description = "Description is required.";
  if (updatedContent.value.type === "banner" && !updatedContent.value.banner)
    errors.value.banner = "Banner image is required.";
  if (
    updatedContent.value.type === "video" &&
    !/^https?:\/\/.+/.test(updatedContent.value.videoLink)
  )
    errors.value.videoLink = "Valid video URL is required.";
  if (!updatedContent.value.content?.trim())
    errors.value.content = "Content is required.";
  return Object.keys(errors.value).length === 0;
};
const clearError = (field) => {
  if (errors.value[field]) {
    delete errors.value[field];
  }
};
const handleUpdateContent = async () => {
  if (loading.value || !validateInputs()) return;
  loading.value = true;
  try {
    const response = await axios.put(getBaseUrl() + `/core/content`, {
      id: updatedContent.value.id,
      title: updatedContent.value.title,
      description: updatedContent.value.description,
      fileLink: updatedContent.value.fileLink,
      fileTitle: updatedContent.value.fileTitle,
      fileButtonText: updatedContent.value.fileButtonText,
      banner: updatedContent.value.banner,
      type: updatedContent.value.type,
      videoLink: updatedContent.value.videoLink,
      content: updatedContent.value.content,
    });
    window.alert("Content Updated Successfully");
  } catch (err) {
    console.log(err);
    window.alert(
      "There was an error update the content. Please Content a developer",
    );
  } finally {
    loading.value = false;
    updateDialog();
  }
};

onMounted(() => {
  if (props.openDialog) {
    openDialog();
  }
});

watch(
  () => props.openDialog,
  (newVal) => {
    if (newVal) {
      openDialog();
    } else {
      closeDialog();
    }
  },
);
watch(
  () => props.initialContentData,
  (newVal) => {
    updatedContent.value = { ...newVal }; // Update updatedContent when initialContentData changes
  },
);
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
}

.dialog {
  background: white;
  padding: 20px;
  border-radius: 0; /* Remove border-radius for full screen */
  width: 100%;
  height: 100%; /* Make dialog full height */
  max-width: 100%; /* Remove max-width constraint */
  max-height: 100%; /* Remove max-height constraint */
  box-shadow: none; /* Remove box-shadow */
  position: relative;
  animation: slide-down 0.3s ease-out;
  display: flex;
  flex-direction: column; /* Ensure content is vertically aligned */
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.dialog-body {
  flex: 1; /* Allow body to take up remaining space */
  overflow-y: auto;
}

.close-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

.close-button i {
  font-size: 20px;
  color: #333;
}

.card {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.form-label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-group.has-error .form-control {
  border-color: red;
}

.error-text {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}

.create-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.update-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
